import { toast } from "sonner";
import { useIssuesStore, usePMStore, useProjectMembersStore } from "@/stores/pm/hooks/usePMStore";
import { useStores } from "@/stores/useStores";
import * as pmRepo from "@/stores/pm/repository";
import type { IssueStatus, PMIssue } from "@/domains/pm/types";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import { useScheduleChangeApi } from "./schedule/scheduleChangeContext";
import { usePermissions } from "@/hooks/usePermissions";
import { usePermissionDenied } from "@/hooks/usePermissionDenied";

/**
 * WHA-3428 — apply callbacks shared by the issue right-click context menu
 * and the toolbar "Actions" dropdown. Every action targets a list of
 * issues (one issue for a single right-click, all selected issues for a
 * multi-selection), so the same logic serves both surfaces.
 */
export function useIssueMenuActions() {
  const issuesStore = useIssuesStore();
  const pmStore = usePMStore();
  const projectMembers = useProjectMembersStore();
  const rootStore = useStores();
  const { t } = useLanguage();
  const schedule = useScheduleChangeApi();
  const { canManagePMOnProject } = usePermissions();
  const denyPermission = usePermissionDenied();

  /**
   * Every action here writes the issues, which needs MANAGE_PM on each one's
   * project (and on the destination, for a move). The menus offer them to
   * everyone, so a member who may not is refused with a message naming who can
   * grant it, instead of the write failing at RLS.
   */
  const mayEdit = (issues: readonly PMIssue[], alsoProjectId?: number): boolean => {
    const projectIds = issues.map((i) => i.building_project_id);
    if (alsoProjectId !== undefined) projectIds.push(alsoProjectId);
    const blocked = projectIds.find((pid) => !canManagePMOnProject(pid));
    if (blocked === undefined) return true;
    void denyPermission("manageIssues", blocked);
    return false;
  };

  // Keep the inline-expanded editor in sync when the edited issue is open.
  const syncSelected = (issues: PMIssue[]) => {
    const sel = issuesStore.selectedIssue;
    if (sel && issues.some((i) => i.id === sel.id)) {
      issuesStore.selectIssue(sel.id);
    }
  };

  const applyStatus = async (issues: PMIssue[], buildingId: number, status: IssueStatus) => {
    if (!mayEdit(issues)) return;
    try {
      await issuesStore.bulkUpdate(
        issues.map((i) => i.id),
        { status },
        buildingId
      );
      syncSelected(issues);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update status");
    }
  };

  /**
   * Write one date across the targeted issues, cascading along dependencies.
   *
   * A single issue goes through the shared schedule path, so it gets the same
   * confirmation dialog as a Gantt drag — the same edit must not behave
   * differently depending on where it was made.
   *
   * A multi-issue selection cannot: one combined preview would have to
   * reconcile several independent cascades before any of them is written, and
   * asking N times is not bulk editing. It writes the whole selection in one
   * statement, then cascades each issue that drives others and reports the
   * total afterwards.
   */
  const applyDate = async (
    issues: PMIssue[],
    buildingId: number,
    field: "start_date" | "due_date",
    value: string | null,
    failureMessage: string
  ) => {
    if (!mayEdit(issues)) return;
    try {
      if (issues.length === 1 && schedule) {
        await schedule.request(issues[0], { [field]: value });
        syncSelected(issues);
        return;
      }

      await issuesStore.bulkUpdate(
        issues.map((i) => i.id),
        { [field]: value },
        buildingId
      );

      if (schedule) {
        // Only the issues that actually drive something need a round trip.
        // In a typical selection that is none of them, and this costs nothing.
        const drivers = issues.filter((i) => schedule.hasDependents(i.id));
        let moved = 0;
        let held = 0;
        for (const issue of drivers) {
          const next = { ...issue, [field]: value } as PMIssue;
          const result = await schedule.applyWithCascade(issue, next.start_date, next.due_date);
          if (!result) continue;
          moved += result.moved.length;
          held += result.warnings.length;
        }
        if (moved > 0 || held > 0) {
          toast.info(
            fill(t.pm.issues.schedule.bulkSummary, {
              moved: String(moved),
              held: String(held),
            })
          );
          await issuesStore.loadIssues(buildingId);
        }
      }
      syncSelected(issues);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : failureMessage);
    }
  };

  const applyDueDate = (issues: PMIssue[], buildingId: number, dueDate: string | null) =>
    applyDate(issues, buildingId, "due_date", dueDate, "Failed to update due date");

  const applyStartDate = (issues: PMIssue[], buildingId: number, startDate: string | null) =>
    applyDate(issues, buildingId, "start_date", startDate, "Failed to update start date");

  /**
   * Move the targeted issues to another project (WHA — issue #11). Only
   * issues not already in `projectId` are written, so re-picking the current
   * project of a uniform selection is a no-op. Because the list is scoped to
   * the page's project filter, a moved issue may no longer belong in view, so
   * reload afterwards to drop it (the optimistic patch alone would leave it
   * showing under the wrong scope).
   */
  const applyMoveToProject = async (issues: PMIssue[], buildingId: number, projectId: number) => {
    const targets = issuesToMove(issues, projectId);
    if (targets.length === 0) return;
    if (!mayEdit(targets, projectId)) return;
    try {
      await issuesStore.bulkUpdate(
        targets.map((i) => i.id),
        { building_project_id: projectId },
        buildingId
      );
      await issuesStore.loadIssues(buildingId);
      syncSelected(issues);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to move to project");
    }
  };

  /**
   * `newIds` is the full selection emitted by the picker list. For a single
   * issue it is applied verbatim (replace). For several issues it is diffed
   * against `prevCommonIds` (the intersection shown in the list) to find the
   * one toggled id, which is then added/removed on every targeted issue —
   * non-destructive across a mixed selection.
   */
  const applyAssignees = async (
    issues: PMIssue[],
    buildingId: number,
    newIds: number[],
    prevCommonIds: number[]
  ) => {
    if (!mayEdit(issues)) return;
    try {
      if (issues.length === 1) {
        // WHA-3448: optimistic — patch the list row + expanded view
        // immediately (resolving ids to user objects), then persist. No
        // reload on success, so the change shows instantly everywhere.
        // WHA-3507: resolve UserProfile shapes from the project-scoped
        // cache (populated when the picker opened) rather than the org
        // store, otherwise subcontractors editing on their own project
        // would render blank avatars during the optimistic window.
        const candidates = projectMembers.get(issues[0].building_project_id);
        const byId = new Map(candidates.map((u) => [u.id, u]));
        const next = newIds
          .map((id) => byId.get(id))
          .filter((u): u is NonNullable<typeof u> => u != null);
        issuesStore.updateIssueOptimistic(issues[0].id, { assignees: next });
        await pmRepo.setIssueAssignees(issues[0].id, newIds);
        // …unless an assignee filter is active, in which case the write may
        // have moved the row off the page and only a refetch reconciles the
        // list with `hiddenByFilterCount`.
        await issuesStore.reconcileJunctionWrite("assignees");
        return;
      }
      const added = newIds.find((id) => !prevCommonIds.includes(id));
      const removed = prevCommonIds.find((id) => !newIds.includes(id));
      await Promise.all(
        issues.map(async (issue) => {
          const current = (issue.assignees ?? []).map((a) => a.id);
          let next = current;
          if (added != null && !current.includes(added)) next = [...next, added];
          if (removed != null) next = next.filter((id) => id !== removed);
          if (next.length !== current.length) {
            await pmRepo.setIssueAssignees(issue.id, next);
          }
        })
      );
      await issuesStore.loadIssues(buildingId);
      syncSelected(issues);
    } catch (e) {
      await issuesStore.loadIssues(buildingId);
      syncSelected(issues);
      toast.error(e instanceof Error ? e.message : "Failed to update assignees");
    }
  };

  const applyDisciplines = async (
    issues: PMIssue[],
    buildingId: number,
    newIds: number[],
    prevCommonIds: number[]
  ) => {
    if (!mayEdit(issues)) return;
    try {
      if (issues.length === 1) {
        // WHA-3448: optimistic — patch row + expanded view immediately.
        const next = pmStore.disciplines.filter((d) => newIds.includes(d.id));
        issuesStore.updateIssueOptimistic(issues[0].id, { disciplines: next });
        await pmRepo.setIssueDisciplines(issues[0].id, newIds);
        // Same as assignees: a discipline filter can be escaped by this write.
        await issuesStore.reconcileJunctionWrite("disciplines");
        return;
      }
      const added = newIds.find((id) => !prevCommonIds.includes(id));
      const removed = prevCommonIds.find((id) => !newIds.includes(id));
      await Promise.all(
        issues.map(async (issue) => {
          const current = (issue.disciplines ?? []).map((d) => d.id);
          let next = current;
          if (added != null && !current.includes(added)) next = [...next, added];
          if (removed != null) next = next.filter((id) => id !== removed);
          if (next.length !== current.length) {
            await pmRepo.setIssueDisciplines(issue.id, next);
          }
        })
      );
      await issuesStore.loadIssues(buildingId);
      syncSelected(issues);
    } catch (e) {
      await issuesStore.loadIssues(buildingId);
      syncSelected(issues);
      toast.error(e instanceof Error ? e.message : "Failed to update disciplines");
    }
  };

  // WHA-3336: attach the targeted issues as chat context and open the
  // sidebar so the user can ask Franklin about them.
  const askFranklin = (issues: PMIssue[]) => {
    issues.forEach((i) =>
      rootStore.chatStore.addAttachedIssue({
        id: i.id,
        issue_number: i.issue_number,
        title: i.title,
      })
    );
    rootStore.chatStore.setViewMode("sidebar");
  };

  return {
    mayEdit,
    applyStatus,
    applyStartDate,
    applyDueDate,
    applyAssignees,
    applyDisciplines,
    applyMoveToProject,
    askFranklin,
  };
}

/** Intersection of a numeric attribute id across several issues. */
export function commonIds(lists: number[][]): number[] {
  if (lists.length === 0) return [];
  return lists.reduce((acc, list) => acc.filter((id) => list.includes(id)));
}

/**
 * The single project shared by every issue, or `undefined` when the
 * selection spans more than one project (or is empty). Drives the check
 * mark shown against the current project in the move-to-project submenu.
 */
export function commonProjectId(issues: PMIssue[]): number | undefined {
  if (issues.length === 0) return undefined;
  const first = issues[0].building_project_id;
  return issues.every((i) => i.building_project_id === first) ? first : undefined;
}

/** Issues that would actually change project when moved to `projectId`. */
export function issuesToMove(issues: PMIssue[], projectId: number): PMIssue[] {
  return issues.filter((i) => i.building_project_id !== projectId);
}
